import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

/// The page's progress for the loading bar: a navigation and the requests the new page makes while it opens.
/// Requests count only from the navigation's start until shortly after its end (the page's own first loads), so
/// background polling (the bell, the inbox) never runs the bar.
@Injectable({ providedIn: 'root' })
export class PageProgressService {
    /// true while a navigation or its page's first requests are running.
    readonly busy$ = new BehaviorSubject<boolean>(false);

    private navigating = false;
    private pending = 0;
    /// Requests started before this time belong to the page being opened.
    private windowEnds = 0;
    private static readonly AFTER_END_MS = 900;
    private settleTimer: any = null;

    constructor(router: Router) {
        router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this.navigating = true;
                this.windowEnds = Number.MAX_SAFE_INTEGER;
                this.update();
            } else if (event instanceof NavigationEnd || event instanceof NavigationError || event instanceof NavigationCancel) {
                this.navigating = false;
                this.windowEnds = Date.now() + PageProgressService.AFTER_END_MS;
                this.update();
            }
        });
    }

    /// A request is starting: true when it counts (call done() when it ends).
    begin(): boolean {
        if (Date.now() > this.windowEnds) { return false; }
        this.pending++;
        this.update();
        return true;
    }

    done(): void {
        this.pending = Math.max(0, this.pending - 1);
        this.update();
    }

    /// Busy at once; idle only after a short pause, so a page whose requests chain (one after another) keeps one bar.
    private update(): void {
        const busy = this.navigating || this.pending > 0;
        if (busy) {
            clearTimeout(this.settleTimer);
            this.settleTimer = null;
            if (!this.busy$.value) { this.busy$.next(true); }
        } else if (this.busy$.value && !this.settleTimer) {
            this.settleTimer = setTimeout(() => {
                this.settleTimer = null;
                if (!this.navigating && this.pending === 0) { this.busy$.next(false); }
            }, 120);
        }
    }
}
